import type { Prismic } from '@landsbankinn/prismic';
import {
  fallbackImage,
  fallbackRichText,
  fallbackText,
  getDataFromLink,
  PrismicLink,
} from '@landsbankinn/prismic';
import { Segment, useMedia } from '@landsbankinn/ui';
import isEmpty from 'lodash.isempty';
import React, { useContext } from 'react';
import { ArticleCardGroup } from '../components/card-group/ArticleCardGroup';
import { Card } from '../components/card/Card';
import { HorizontalCard } from '../components/card/HorizontalCard';

interface IProps {
  align: string;
  fields: [IField];
}

interface IField {
  image: Prismic.Image & {
    ar_1_1: Prismic.Image;
  };
  percentage?: number;
  heading: Prismic.RichText;
  copy: Prismic.RichText;
  link: Prismic.Link;
  isReverse?: boolean;
  isHorizontal?: boolean;
  style: any;
}

const CardItem = ({
  image,
  heading,
  copy,
  link,
  isReverse,
  isHorizontal,
  percentage,
  style,
}: IField) => {
  const linkdata = getDataFromLink(link);
  const title = fallbackText(heading, linkdata.title ?? undefined);

  const card = (
    <Card
      link={link}
      title={
        link ? (
          <PrismicLink value={link} title={title ?? undefined}>
            <a>{title}</a>
          </PrismicLink>
        ) : (
          title
        )
      }
      description={fallbackRichText(copy, linkdata.abstract ?? undefined)}
      image={fallbackImage(image, linkdata.image ?? undefined)}
      ar="ar_1_1"
      padding={true}
      backgroundAlt={true}
      // action={
      //   <PrismicLink value={link} title={title}>
      //     <ArrowButton hideText />
      //   </PrismicLink>
      // }
      style={style}
      percentage={percentage}
      hasTitleMinHeight={true}
    />
  );

  const horizontalCard = (
    <HorizontalCard
      link={link}
      title={fallbackText(heading, linkdata.title ?? undefined) ?? undefined}
      description={fallbackRichText(copy, linkdata.abstract ?? undefined)}
      image={fallbackImage(image, linkdata.image ?? undefined)}
      reverse={isReverse}
      backgroundAlt={true}
      style={style}
    />
  );

  if (!link && isHorizontal) {
    return horizontalCard;
  }

  if (isHorizontal) {
    return horizontalCard;
  }

  if (!link) {
    return card;
  }

  return (
    <PrismicLink
      value={link}
      title={fallbackText(heading, linkdata.title ?? undefined) ?? undefined}
    >
      {card}
    </PrismicLink>
  );
};

export const ArticleContainer = ({ align, fields }: IProps) => {
  const isTablet = useMedia('md');
  const count: number = fields?.length ?? 0;

  if (isEmpty(fields)) {
    return null;
  }

  return (
    <Segment paddingBottomMobile={0}>
      <ArticleCardGroup>
        {fields?.map((item, index) => {
          return (
            <CardItem
              key={`card-item-${index}`}
              isReverse={align === 'Left' && index % 2 === 0}
              isHorizontal={
                count === 1 ||
                count === 2 ||
                (count === 4 && index === 0) ||
                (count === 3 && index === 2 && isTablet)
              }
              percentage={
                count === 3 || count > 4 || (count === 4 && index > 0)
                  ? 0.33
                  : 1
              }
              {...item}
            />
          );
        })}
      </ArticleCardGroup>
    </Segment>
  );
};
